if (!customElements.get('product-configurator')) {
  customElements.define(
    'product-configurator',
    class ProductConfigurator extends HTMLElement {
      constructor() {
        super();

        this.variants = this.parseJson('[data-configurator-variants]') || [];
        this.rules = this.parseJson('[data-configurator-rules]') || {
          placements: [],
          quantityTiers: [],
          leadTimes: [],
          media: [],
          colorValues: [],
          currency: 'EUR',
          locale: document.documentElement.lang || 'en',
          strings: {},
        };
        this.rules.strings = this.rules.strings || {};
        this.rules.quantityTiers = [...this.rules.quantityTiers].sort((a, b) => a.minQuantity - b.minQuantity);
        this.rules.leadTimes = [...this.rules.leadTimes].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        this.colorPosition = parseInt(this.dataset.colorPosition, 10) || null;
        this.sizePosition = parseInt(this.dataset.sizePosition, 10) || null;

        this.minCombined = this.rules.minCombinedQuantity || this.rules.quantityTiers[0]?.minQuantity || 1;

        this.allVariantMediaIds = new Set(
          this.variants.filter((v) => v.featuredMediaId).map((v) => String(v.featuredMediaId))
        );

        // Photo colour from its alt text: the colour name as a whole word ("embroidered" is not "Red"), the longest
        // name first ("Heather Grey" wins over "Grey"), whatever the order of the colours on the product.
        this.mediaColorByAlt = new Map();
        const colorsLongestFirst = [...(this.rules.colorValues || [])].sort((a, b) => b.length - a.length);
        const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        (this.rules.media || []).forEach((media) => {
          const alt = media.alt || '';
          const matchedColor = colorsLongestFirst.find((color) =>
            new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(color.trim())}($|[^\\p{L}\\p{N}])`, 'iu').test(alt)
          );
          if (matchedColor) this.mediaColorByAlt.set(String(media.id), matchedColor);
        });

        this.state = {
          color: null,
          sizeQuantities: new Map(), // sizeValue -> quantity (> 0)
          printMethod: null,
          placements: new Set(),
          leadTimeId: null,
          production: 'standard',
        };
        this.bulkGroupId = null;
        this.submitting = false;
      }

      connectedCallback() {
        this.cacheElements();
        this.bindEvents();
        this.hydrateInitialState();
        this.renderSizeRows();
        this.recalculate();
        this.renderPriceRange();
        this.loadRemainingVariants();
      }

      // Liquid only lists a product's first 250 variants (a product can have up to 2,048): the next ones are loaded
      // page by page from the configurator-variants section, then the sizes and stock are drawn again, keeping the
      // quantities already entered.
      async loadRemainingVariants() {
        const total = parseInt(this.dataset.variantsCount, 10) || 0;
        if (total <= this.variants.length || !this.dataset.productUrl) return;

        const known = new Set(this.variants.map((v) => String(v.id)));
        const pages = Math.ceil(total / 250);
        for (let page = 2; page <= pages; page += 1) {
          try {
            const url = new URL(this.dataset.productUrl, window.location.origin);
            url.searchParams.set('section_id', 'configurator-variants');
            url.searchParams.set('page', page);
            const response = await fetch(url);
            if (!response.ok) break;
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const variants = JSON.parse(html.querySelector('[data-configurator-variants-page]')?.textContent || '[]');
            variants.forEach((variant) => {
              if (known.has(String(variant.id))) return;
              known.add(String(variant.id));
              this.variants.push(variant);
              if (variant.featuredMediaId) this.allVariantMediaIds.add(String(variant.featuredMediaId));
            });
          } catch {
            break; // the variants loaded so far stay usable
          }
        }

        this.renderSizeRows();
        this.recalculate();
        this.updateMediaFilter(false);
      }

      /* ---------------------------------------------------------------- utils */

      parseJson(selector) {
        try {
          return JSON.parse(this.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      str(key, count) {
        const value = (this.rules.strings || {})[key] || '';
        return count == null ? value : value.replace('%count%', count);
      }

      get combinedQuantity() {
        let total = 0;
        this.state.sizeQuantities.forEach((qty) => (total += qty));
        return total;
      }

      renderPriceRange() {
        const startingPrice = this.dataset.priceRangeMax;
        const el = document.querySelector('[data-configurator-price-range]');
        const priceEl = el?.querySelector('[data-configurator-starting-price]');
        if (!el || !priceEl || !startingPrice) return;
        priceEl.textContent = startingPrice;
        el.hidden = false;
      }

      cacheElements() {
        this.form = this.querySelector('form');

        this.colorButtons = Array.from(this.querySelectorAll('[data-color-option]'));
        this.printMethodButtons = Array.from(this.querySelectorAll('[data-print-method-option]'));
        this.placementsSubsection = this.querySelector('[data-configurator-subsection="placements"]');
        this.sizeRows = Array.from(this.querySelectorAll('[data-size-row]'));
        this.placementRows = Array.from(this.querySelectorAll('[data-placement]'));
        this.placementUploadRows = Array.from(this.querySelectorAll('[data-placement-upload]'));
        this.placementsHint = this.querySelector('[data-placements-hint]');
        this.sizeSurchargeNote = this.querySelector('[data-size-surcharge-note]');
        this.sizeSurchargeText = this.querySelector('[data-size-surcharge-text]');
        this.tierRows = Array.from(this.querySelectorAll('[data-tier-row]'));
        this.leadTimeButtons = Array.from(this.querySelectorAll('[data-leadtime-option]'));
        this.productionButtons = Array.from(this.querySelectorAll('[data-production-option]'));
        this.productionSubsection = this.querySelector('[data-configurator-subsection="production"]');

        this.variantIdField = this.querySelector('[data-configurator-variant-id]');
        this.quantityField = this.querySelector('[data-configurator-quantity-value]');
        this.placementsSummaryField = this.querySelector('[data-configurator-placements-summary]');
        this.leadtimeSummaryField = this.querySelector('[data-configurator-leadtime-summary]');
        this.breakdownField = this.querySelector('[data-configurator-breakdown-summary]');
        this.bulkGroupField = this.querySelector('[data-configurator-bulk-group]');
        this.combinedQtyField = this.querySelector('[data-configurator-combined-qty-field]');
        this.unitPriceField = this.querySelector('[data-configurator-unit-price-field]');
        this.debugSummaryField = this.querySelector('[data-configurator-debug-summary]');

        this.combinedQtyEls = Array.from(this.querySelectorAll('[data-combined-qty]'));
        this.combinedHintEl = this.querySelector('[data-combined-hint]');

        this.totalPriceEl = this.querySelector('[data-total-price]');
        this.unitPriceEls = Array.from(this.querySelectorAll('[data-unit-price]'));
        this.totalPriceOriginalEl = this.querySelector('[data-total-price-original]');
        this.quantityDiscountBadgeEl = this.querySelector('[data-quantity-discount-badge]');
        this.estimatedDateEl = this.querySelector('[data-estimated-date]');
        this.noticeEl = this.querySelector('[data-configurator-notice]');
        this.noticeDefaultText = this.noticeEl?.textContent?.trim();
        this.noticeDesignText = this.str('noticeDesign') || this.noticeDefaultText;
        this.submitButton = this.querySelector('.product-configurator__submit');
        this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessageEl = this.querySelector('.product-form__error-message');

        this.recapDetails = this.querySelector('[data-configurator-recap]');
        this.recapSummary = this.recapDetails?.querySelector('summary');
        this.recapCollapse = this.recapDetails?.querySelector('.product-configurator__recap-collapse');
        if (this.recapDetails?.open) this.recapDetails.classList.add('is-open');

        this.selectedColorLabel = this.querySelector('[data-selected-color-label]');

        this.recapColor = this.querySelector('[data-recap-color]');
        this.recapSize = this.querySelector('[data-recap-size]');
        this.recapPrintMethod = this.querySelector('[data-recap-print-method]');
        this.recapPlacementsEmpty = this.querySelector('[data-recap-placements-empty]');
        this.recapQuantity = this.querySelector('[data-recap-quantity]');
        this.recapLeadtime = this.querySelector('[data-recap-leadtime]');
        this.recapProductionRow = this.querySelector('[data-recap-production-row]');
        this.recapProduction = this.querySelector('[data-recap-production]');

        this.mediaGallery = document.querySelector(`#MediaGallery-${this.dataset.section}`);
        this.galleryViewer = this.mediaGallery?.querySelector('[id^="GalleryViewer-"]');

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.pricingToggle = this.querySelector('[data-pricing-toggle]');
        this.pricingCollapse = this.querySelector('[data-pricing-collapse]');
      }

      bindEvents() {
        this.colorButtons.forEach((btn) => {
          btn.addEventListener('click', () => this.selectColor(btn.dataset.value));
          // Photos of a color start loading as soon as its swatch is hovered or focused, before the click.
          ['pointerenter', 'focus', 'touchstart'].forEach((type) =>
            btn.addEventListener(type, () => this.preloadColorMedia(btn.dataset.value), { passive: true })
          );
        });
        // Then, once the page is loaded, the first photo of every color in the background.
        const preloadAll = () => this.colorButtons.forEach((btn) => this.preloadColorMedia(btn.dataset.value, 1));
        const whenIdle = () => (window.requestIdleCallback ? requestIdleCallback(preloadAll) : setTimeout(preloadAll, 1500));
        if (document.readyState === 'complete') whenIdle();
        else window.addEventListener('load', whenIdle, { once: true });

        this.printMethodButtons.forEach((btn) =>
          btn.addEventListener('click', () => this.selectPrintMethod(btn.dataset.value))
        );
        this.productionButtons.forEach((btn) =>
          btn.addEventListener('click', () => this.selectProduction(btn.dataset.value))
        );

        this.sizeRows.forEach((row) => {
          const value = row.dataset.sizeValue;
          const input = row.querySelector('[data-size-qty-input]');
          const decrease = row.querySelector('[data-size-qty-decrease]');
          const increase = row.querySelector('[data-size-qty-increase]');

          input?.addEventListener('change', () =>
            this.setSizeQuantity(value, parseInt(input.value.replace(/\D/g, ''), 10) || 0)
          );
          decrease?.addEventListener('click', () =>
            this.setSizeQuantity(value, (this.state.sizeQuantities.get(value) || 0) - 1)
          );
          increase?.addEventListener('click', () =>
            this.setSizeQuantity(value, (this.state.sizeQuantities.get(value) || 0) + 1)
          );
        });

        this.placementRows.forEach((row) => {
          const checkbox = row.querySelector('[data-placement-checkbox]');
          checkbox.addEventListener('change', () =>
            this.togglePlacement(row.dataset.placementKey, checkbox.checked)
          );
        });

        this.library = this.parseJson('[data-configurator-library]') || [];

        this.placementUploadRows.forEach((row) => {
          const fileInput = row.querySelector('[data-placement-file]');
          fileInput.addEventListener('change', () => this.validateFile(row, fileInput));
          this.setupLibrary(row, fileInput);

          const removeButton = row.querySelector('[data-placement-file-remove]');
          removeButton?.addEventListener('click', (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            this.clearFile(row, fileInput);
          });

          const sizeGroup = row.querySelector('[data-placement-sizes]');
          if (sizeGroup) {
            const sizeButtons = Array.from(sizeGroup.querySelectorAll('[data-design-size-option]'));
            const sizeValueField = sizeGroup.querySelector('[data-design-size-value]');
            const initial = sizeButtons.find((btn) => btn.getAttribute('aria-pressed') === 'true');
            if (sizeValueField) sizeValueField.value = initial?.dataset.value || '';

            sizeButtons.forEach((btn) => {
              btn.addEventListener('click', () => {
                sizeButtons.forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
                if (sizeValueField) sizeValueField.value = btn.dataset.value;
                this.updateRecap();
                this.checkImageQuality(row);
              });
            });
          }
        });

        this.leadTimeButtons.forEach((btn) =>
          btn.addEventListener('click', () => this.selectLeadTime(btn.dataset.leadtimeId))
        );

        this.recapSummary?.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.toggleRecap();
        });

        this.galleryViewer
          ?.querySelector('[id^="Slider-Gallery-"]')
          ?.addEventListener('click', this.onGalleryImageClick.bind(this));

        this.pricingToggle?.addEventListener('click', () => this.togglePricingTable());

        this.addEventListener('submit', this.handleSubmitCapture.bind(this), true);
      }

      onGalleryImageClick(evt) {
        const slide = evt.target.closest('[id^="Slide-"]');
        if (!slide) return;

        const rect = slide.getBoundingClientRect();
        const clickedOnLeftHalf = evt.clientX - rect.left < rect.width / 2;
        const button = this.galleryViewer.querySelector(
          `button[name="${clickedOnLeftHalf ? 'previous' : 'next'}"]`
        );
        if (button && !button.disabled) button.click();
      }

      hydrateInitialState() {
        const initialColor = this.colorButtons.find((btn) => btn.getAttribute('aria-pressed') === 'true');
        this.state.color = initialColor?.dataset.value || this.colorButtons[0]?.dataset.value || null;

        const initialLeadTime =
          this.rules.leadTimes.find((lt) => lt.isDefault) || this.rules.leadTimes[0] || null;
        this.state.leadTimeId = initialLeadTime?.id != null ? String(initialLeadTime.id) : null;

        this.updatePressedState(this.colorButtons, this.state.color);
        this.updateSelectedColorLabel();

        const initialPrintMethod = this.printMethodButtons.find(
          (btn) => btn.getAttribute('aria-pressed') === 'true'
        );
        this.state.printMethod =
          initialPrintMethod?.dataset.value || this.printMethodButtons[0]?.dataset.value || null;
        this.updatePressedState(this.printMethodButtons, this.state.printMethod);
        this.updatePlacementsVisibility();

        this.updateLeadTimeSelection();
        this.updateMediaFilter(false);
      }

      /* ------------------------------------------------------------- colours */

      updateSelectedColorLabel() {
        if (this.selectedColorLabel) this.selectedColorLabel.textContent = this.state.color || '';
      }

      selectColor(value) {
        this.state.color = value;
        this.updatePressedState(this.colorButtons, value);
        this.updateSelectedColorLabel();
        this.renderSizeRows();
        this.updateMediaFilter(true);
        this.recalculate();
      }

      selectPrintMethod(value) {
        this.state.printMethod = value;
        this.updatePressedState(this.printMethodButtons, value);
        this.updatePlacementsVisibility();
        this.recalculate();
      }

      updatePlacementsVisibility() {
        // Rush production only applies to printed items: "No Printing" hides it.
        if (this.productionSubsection) this.productionSubsection.hidden = this.state.printMethod === 'none';
        if (!this.placementsSubsection) return;
        this.placementsSubsection.hidden = this.state.printMethod === 'none';
      }

      /* ------------------------------------------------------ production time */

      selectProduction(value) {
        this.state.production = value;
        this.updatePressedState(this.productionButtons, value);
        this.recalculate();
      }

      isRush() {
        return Boolean(this.rules.rush) && this.state.printMethod !== 'none' && this.state.production === 'rush';
      }

      // Rush costs 20% of the item's price per piece (garment + prints, before volume discount), rounded to the
      // closest "Rush production" variant (one variant per price, every $0.10).
      rushPerPiece() {
        if (!this.isRush()) return null;
        const pieces = this.combinedQuantity;
        const placementsExtra = this.getSelectedPlacements().reduce((sum, p) => sum + (p.priceModifier || 0), 0);
        let garments = 0;
        this.state.sizeQuantities.forEach((qty, size) => (garments += this.priceForSize(size) * qty));
        const perPiece = pieces > 0 ? garments / pieces + placementsExtra : this.referenceBasePrice() + placementsExtra;
        const wantedCents = Math.round((perPiece * this.rules.rush.percent) / 10) * 10;

        let best = null;
        this.rules.rush.variants.forEach(([cents, id]) => {
          if (!best || Math.abs(cents - wantedCents) < Math.abs(best.cents - wantedCents)) best = { cents, id };
        });
        return best ? { variantId: best.id, price: best.cents / 100 } : null;
      }

      productionLabel() {
        if (!this.rules.rush || this.state.printMethod === 'none') return '';
        const btn = this.productionButtons.find((b) => b.dataset.value === this.state.production);
        const title = btn?.querySelector('strong')?.textContent?.trim();
        const text = btn?.querySelector('span')?.textContent?.split('·')[0]?.trim();
        return title ? (text ? `${title} (${text})` : title) : '';
      }

      // Does this gallery photo belong to this color? (alt text naming the color, or the color's variant photo;
      // photos of no color belong to all of them.)
      mediaMatchesColor(mediaId, color, nativeColorMediaIds) {
        const altTaggedColor = this.mediaColorByAlt.get(mediaId);
        if (altTaggedColor) return altTaggedColor === color;
        if (this.allVariantMediaIds.has(mediaId)) return nativeColorMediaIds.has(mediaId);
        return true;
      }

      colorVariantMediaIds(color) {
        return new Set(
          this.variants
            .filter((v) => v[`option${this.colorPosition}`] === color && v.featuredMediaId)
            .map((v) => String(v.featuredMediaId))
        );
      }

      // Loads the gallery photos of a color ahead of time (they are lazy loaded and hidden until then), so they
      // show up at once when the color is chosen. limit: only the first N photos.
      preloadColorMedia(color, limit = Infinity) {
        if (!this.mediaGallery || !this.colorPosition || !color) return;
        this.preloadedColors = this.preloadedColors || new Map();
        if ((this.preloadedColors.get(color) || 0) >= limit) return;
        this.preloadedColors.set(color, limit);

        const nativeColorMediaIds = this.colorVariantMediaIds(color);
        const prefix = `${this.dataset.section}-`;
        let count = 0;
        this.mediaGallery.querySelectorAll('[data-media-id]').forEach((el) => {
          if (count >= limit) return;
          const rawId = el.dataset.mediaId;
          const mediaId = rawId.startsWith(prefix) ? rawId.slice(prefix.length) : rawId;
          if (!this.mediaColorByAlt.has(mediaId) && !this.allVariantMediaIds.has(mediaId)) return; // shared photo
          if (!this.mediaMatchesColor(mediaId, color, nativeColorMediaIds)) return;
          el.querySelectorAll('img[loading="lazy"]').forEach((img) => {
            img.loading = 'eager';
          });
          count += 1;
        });
      }

      updateMediaFilter(scrollToMedia) {
        if (!this.mediaGallery || !this.colorPosition) return;

        const nativeColorMediaIds = this.colorVariantMediaIds(this.state.color);

        const prefix = `${this.dataset.section}-`;
        let firstMatchId = null;

        this.mediaGallery.querySelectorAll('[data-media-id], [data-target]').forEach((el) => {
          const rawId = el.dataset.mediaId || el.dataset.target;
          const mediaId = rawId.startsWith(prefix) ? rawId.slice(prefix.length) : rawId;
          const matchesCurrentColor = this.mediaMatchesColor(mediaId, this.state.color, nativeColorMediaIds);

          el.hidden = !matchesCurrentColor;
          if (matchesCurrentColor && !firstMatchId) firstMatchId = mediaId;
        });

        if (scrollToMedia && firstMatchId) {
          this.mediaGallery.setActiveMedia?.(`${this.dataset.section}-${firstMatchId}`, true);
        }
      }

      updatePressedState(buttons, value) {
        buttons.forEach((btn) =>
          btn.setAttribute('aria-pressed', btn.dataset.value === value ? 'true' : 'false')
        );
      }

      /* --------------------------------------------------------------- sizes */

      matchVariant(color, size) {
        return this.variants.find((variant) => {
          const matchesColor = this.colorPosition ? variant[`option${this.colorPosition}`] === color : true;
          const matchesSize = this.sizePosition ? variant[`option${this.sizePosition}`] === size : true;
          return matchesColor && matchesSize;
        });
      }

      variantForSize(size) {
        return this.matchVariant(this.state.color, size);
      }

      stockForSize(size) {
        const variant = this.variantForSize(size);
        if (!variant || variant.available === false) return 0;
        if (variant.inventory_management !== 'shopify') return Infinity;
        if (variant.inventory_policy === 'continue') return Infinity;
        return Math.max(variant.inventory_quantity, 0);
      }

      renderSizeRows() {
        const threshold = 10;
        const displayCap = 500;

        this.sizeRows.forEach((row) => {
          const value = row.dataset.sizeValue;
          const variant = this.variantForSize(value);
          const stock = this.stockForSize(value);
          const input = row.querySelector('[data-size-qty-input]');
          const decrease = row.querySelector('[data-size-qty-decrease]');
          const increase = row.querySelector('[data-size-qty-increase]');
          const stockLabel = row.querySelector('[data-size-stock-label]');

          row.classList.remove('is-in-stock', 'is-low-stock', 'is-out-of-stock');

          const managed =
            variant?.inventory_management === 'shopify' && variant?.inventory_policy !== 'continue';
          const unavailable = !variant || variant.available === false || stock <= 0;

          if (unavailable) {
            row.classList.add('is-out-of-stock');
            this.state.sizeQuantities.delete(value);
            if (input) {
              input.value = '0';
              input.disabled = true;
            }
            if (decrease) decrease.disabled = true;
            if (increase) increase.disabled = true;
            if (stockLabel) stockLabel.textContent = '0';
            return;
          }

          if (input) input.disabled = false;

          let qty = this.state.sizeQuantities.get(value) || 0;
          if (Number.isFinite(stock)) qty = Math.min(qty, stock);
          if (qty > 0) this.state.sizeQuantities.set(value, qty);
          else this.state.sizeQuantities.delete(value);
          if (input) input.value = String(qty);

          if (managed) {
            const count = Math.max(variant.inventory_quantity, 0);
            row.classList.add(count <= threshold ? 'is-low-stock' : 'is-in-stock');
            if (stockLabel) {
              // Large supplier stocks are shown as "500+": a readable figure, while Shopify keeps the exact stock.
              const shown = count > displayCap ? `${displayCap}+` : count.toLocaleString(this.rules.locale);
              stockLabel.textContent = this.str('stockCount', shown);
            }
          } else {
            row.classList.add('is-in-stock');
            if (stockLabel) stockLabel.textContent = this.str('inStock');
          }

          if (decrease) decrease.disabled = qty <= 0;
          if (increase) increase.disabled = Number.isFinite(stock) && qty >= stock;
        });
      }

      setSizeQuantity(size, requested) {
        const stock = this.stockForSize(size);
        let qty = Number.isFinite(requested) ? requested : 0;
        qty = Math.max(0, qty);
        let wasClamped = false;
        if (Number.isFinite(stock) && qty > stock) {
          qty = stock;
          wasClamped = true;
        }

        if (qty > 0) this.state.sizeQuantities.set(size, qty);
        else this.state.sizeQuantities.delete(size);

        const row = this.sizeRows.find((r) => r.dataset.sizeValue === size);
        const input = row?.querySelector('[data-size-qty-input]');
        const decrease = row?.querySelector('[data-size-qty-decrease]');
        const increase = row?.querySelector('[data-size-qty-increase]');
        if (input) {
          input.value = String(qty);
          if (wasClamped) {
            input.classList.remove('is-shaking');
            void input.offsetWidth; // restart the animation even if it's already playing
            input.classList.add('is-shaking');
            input.addEventListener(
              'animationend',
              () => input.classList.remove('is-shaking'),
              { once: true }
            );
          }
        }
        if (decrease) decrease.disabled = qty <= 0;
        if (increase) increase.disabled = Number.isFinite(stock) && qty >= stock;

        this.recalculate();
      }

      /* ---------------------------------------------------------- placements */

      togglePlacement(key, isChecked) {
        const uploadRow = this.placementUploadRows.find((row) => row.dataset.placementKey === key);
        const fileInput = uploadRow?.querySelector('[data-placement-file]');

        if (isChecked) {
          this.state.placements.add(key);
          if (uploadRow) {
            uploadRow.hidden = false;
            requestAnimationFrame(() => uploadRow.classList.add('is-open'));
          }
          if (fileInput) fileInput.disabled = false;
        } else {
          this.state.placements.delete(key);
          if (uploadRow) {
            uploadRow.classList.remove('is-open');
            uploadRow.hidden = true;
          }
          if (fileInput) {
            fileInput.disabled = true;
            fileInput.value = '';
          }
          if (uploadRow) {
            uploadRow.classList.remove('has-error');
            this.resetFileDisplay(uploadRow);
          }
        }

        this.recalculate();
      }


      // File type from the browser, or from the extension when the browser doesn't give one.
      // Only the formats announced next to the upload zone: PNG, JPG, PDF and SVG.
      fileType(file) {
        const allowed = ['image/png', 'image/jpeg', 'application/pdf', 'image/svg+xml'];
        if (allowed.includes(file.type)) return file.type;
        const extension = (file.name.split('.').pop() || '').toLowerCase();
        return { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', pdf: 'application/pdf', svg: 'image/svg+xml' }[extension] || null;
      }

      /* ------------------------------------------------- files from past orders */

      // "Choose from my files": the artwork the customer sent with past orders, reused without uploading it again.
      setupLibrary(row, fileInput) {
        const wrapper = row.querySelector('[data-library]');
        if (!wrapper || !this.library.length) return;
        wrapper.hidden = false;

        const toggle = wrapper.querySelector('[data-library-toggle]');
        const panel = wrapper.querySelector('[data-library-panel]');
        const grid = wrapper.querySelector('[data-library-grid]');

        this.library.forEach((entry, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'product-configurator__library-item';
          button.dataset.libraryIndex = String(index);
          button.title = entry.name;

          const preview = document.createElement('span');
          preview.className = 'product-configurator__library-preview';
          if (entry.isImage) {
            const img = document.createElement('img');
            img.src = entry.url + (entry.url.includes('?') ? '&' : '?') + 'width=200';
            img.alt = '';
            img.loading = 'lazy';
            preview.append(img);
          } else {
            preview.textContent = (entry.name.split('.').pop() || 'file').toUpperCase();
          }
          const name = document.createElement('span');
          name.className = 'product-configurator__library-name';
          name.textContent = entry.name;
          button.append(preview, name);

          button.addEventListener('click', () => {
            this.useLibraryFile(row, fileInput, entry);
            panel.hidden = true;
            toggle.setAttribute('aria-expanded', 'false');
          });
          grid.append(button);
        });

        toggle.addEventListener('click', () => {
          const willOpen = panel.hidden;
          panel.hidden = !willOpen;
          toggle.setAttribute('aria-expanded', String(willOpen));
        });
      }

      useLibraryFile(row, fileInput, entry) {
        fileInput.value = '';
        row.classList.remove('has-error');
        row.dataset.libraryUrl = entry.url;
        row.dataset.libraryName = entry.name;
        row.querySelectorAll('[data-library-index]').forEach((button) => {
          button.classList.toggle('is-selected', this.library[Number(button.dataset.libraryIndex)] === entry);
        });

        const fileNameEl = row.querySelector('[data-placement-filename]');
        const removeButton = row.querySelector('[data-placement-file-remove]');
        const trigger = row.querySelector('[data-placement-file-trigger]');
        if (fileNameEl) fileNameEl.textContent = entry.name;
        if (removeButton) removeButton.hidden = false;
        if (trigger) trigger.hidden = true;

        // Same quality check as an upload: read the image width from the stored file.
        delete row.dataset.imageWidth;
        this.checkImageQuality(row);
        if (entry.isImage) {
          const image = new Image();
          image.onload = () => {
            if (row.dataset.libraryUrl !== entry.url) return; // choice changed meanwhile
            row.dataset.imageWidth = String(image.naturalWidth);
            this.checkImageQuality(row);
          };
          image.src = entry.url;
        }
        this.recalculate();
      }

      clearLibraryChoice(row) {
        delete row.dataset.libraryUrl;
        delete row.dataset.libraryName;
        row.querySelectorAll('[data-library-index].is-selected').forEach((button) => button.classList.remove('is-selected'));
      }

      // Artwork chosen from past orders for a placement: { url, name } or null.
      libraryChoice(key) {
        const row = this.placementUploadRows?.find((r) => r.dataset.placementKey === key);
        return row?.dataset.libraryUrl ? { url: row.dataset.libraryUrl, name: row.dataset.libraryName } : null;
      }

      resetFileDisplay(row) {
        this.clearLibraryChoice(row);
        delete row.dataset.imageWidth;
        this.checkImageQuality(row);
        const fileNameEl = row.querySelector('[data-placement-filename]');
        const removeButton = row.querySelector('[data-placement-file-remove]');
        const trigger = row.querySelector('[data-placement-file-trigger]');
        if (fileNameEl) fileNameEl.textContent = '';
        if (removeButton) removeButton.hidden = true;
        if (trigger) trigger.hidden = false;
      }

      validateFile(row, input) {
        const file = input.files[0];
        const fileNameEl = row.querySelector('[data-placement-filename]');
        const removeButton = row.querySelector('[data-placement-file-remove]');
        const trigger = row.querySelector('[data-placement-file-trigger]');
        row.classList.remove('has-error');

        if (!file) {
          this.resetFileDisplay(row);
          this.recalculate();
          return;
        }

        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize || !this.fileType(file)) {
          row.classList.add('has-error');
          input.value = '';
          this.resetFileDisplay(row);
        } else {
          this.clearLibraryChoice(row);
          if (fileNameEl) fileNameEl.textContent = file.name;
          if (removeButton) removeButton.hidden = false;
          if (trigger) trigger.hidden = true;
          this.readImageWidth(row, file);
          this.preUpload(row.dataset.placementKey, file);
        }
        this.recalculate();
      }

      /* ------------------------------------------------------ image quality */

      // Pixel width of a PNG/JPG; vector files (SVG, PDF) scale without losing quality.
      readImageWidth(row, file) {
        delete row.dataset.imageWidth;
        this.checkImageQuality(row);
        if (!/^image\/(png|jpeg)$/.test(this.fileType(file))) return;

        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
          URL.revokeObjectURL(url);
          if (this.placementFile(row.dataset.placementKey) !== file) return; // file changed meanwhile
          row.dataset.imageWidth = String(image.naturalWidth);
          this.checkImageQuality(row);
        };
        image.onerror = () => URL.revokeObjectURL(url);
        image.src = url;
      }

      // Print width in inches of the selected design size, read from its label ("width ~3 in", "7,5 cm").
      printWidthInches(row) {
        const pressed = row.querySelector('[data-design-size-option][aria-pressed="true"]');
        if (!pressed) return null;
        const match = pressed.textContent.match(/(\d+(?:[.,]\d+)?)\s*(in\b|inch|inches|"|cm)/i);
        if (match) {
          const value = parseFloat(match[1].replace(',', '.'));
          return /cm/i.test(match[2]) ? value / 2.54 : value;
        }
        return { small: 3, medium: 5, large: 8 }[pressed.dataset.value] || null;
      }

      checkImageQuality(row) {
        const note = row.querySelector('[data-placement-quality]');
        if (!note) return;
        const width = parseInt(row.dataset.imageWidth, 10);
        const inches = this.printWidthInches(row);
        if (!width || !inches) {
          note.hidden = true;
          return;
        }

        const dpi = Math.round(width / inches);
        const recommended = Math.ceil(inches * 300);
        const pressed = row.querySelector('[data-design-size-option][aria-pressed="true"]');
        const sizeName = pressed?.querySelector('strong')?.textContent || 'this';
        // Suggest a smaller size only if one exists (sizes are listed from smallest to largest).
        const smallerSize = pressed && pressed !== row.querySelector('[data-design-size-option]');
        note.classList.toggle('is-low', dpi < 150);
        note.hidden = dpi >= 300;
        note.querySelector('[data-placement-quality-text]').textContent =
          dpi < 150
            ? `Low resolution: your image is ${width} px wide (${dpi} DPI) for a ${sizeName} print. It may look blurry, use an image at least ${recommended} px wide${smallerSize ? ' or choose a smaller size' : ''}.`
            : `Your image is ${width} px wide (${dpi} DPI) for a ${sizeName} print: acceptable, but ${recommended} px wide (300 DPI) will print sharper.`;
      }

      clearFile(row, input) {
        input.value = '';
        delete row.dataset.imageWidth;
        this.checkImageQuality(row);
        row.classList.remove('has-error');
        this.resetFileDisplay(row);
        this.recalculate();
      }

      /* ---------------------------------------------------------- lead times */

      selectLeadTime(id) {
        this.state.leadTimeId = id;
        this.updateLeadTimeSelection();
        this.recalculate();
      }

      updateLeadTimeSelection() {
        this.leadTimeButtons.forEach((btn) => {
          const isSelected = btn.dataset.leadtimeId === this.state.leadTimeId;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        const selected = this.rules.leadTimes.find((lt) => String(lt.id) === this.state.leadTimeId);
        if (this.estimatedDateEl) {
          this.estimatedDateEl.textContent = this.formatEstimatedDate(selected);
        }
      }

      formatEstimatedDate(leadTime) {
        if (!leadTime?.durationDays) return leadTime?.durationLabel || '';

        const date = new Date();
        date.setDate(date.getDate() + leadTime.durationDays);

        try {
          return new Intl.DateTimeFormat(this.rules.locale, {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
          }).format(date);
        } catch {
          return date.toLocaleDateString();
        }
      }

      getSelectedLeadTime() {
        return this.rules.leadTimes.find((lt) => String(lt.id) === this.state.leadTimeId) || null;
      }

      getSelectedPlacements() {
        return this.rules.placements.filter((p) => this.state.placements.has(p.key));
      }

      placementFileName(key) {
        const row = this.placementUploadRows?.find((r) => r.dataset.placementKey === key);
        return row?.querySelector('[data-placement-file]')?.files?.[0]?.name || this.libraryChoice(key)?.name || null;
      }

      // One "Custom printing" line per selected placement, for as many pieces as the item: checkout charges it.
      // The artwork file is attached to this line (its address when already sent in the background).
      placementItems(sharedText, uploadedUrls = {}) {
        const pieces = this.combinedQuantity;
        if (!pieces || this.state.printMethod === 'none') return [];
        const forLabel = [this.rules.productTitle, this.state.color].filter(Boolean).join(' – ');

        return this.getSelectedPlacements()
          .filter((p) => p.variantId)
          .map((p) => {
            const properties = { For: forLabel, '_Bulk group': sharedText['_Bulk group'], _Print: this.placementPrintLabel(p) };
            const fileName = this.placementFileName(p.key);
            if (fileName) properties['_Design file'] = fileName;
            // A file already sent in the background, or from a past order, is sent as its address; otherwise the
            // file itself.
            const uploadedUrl = uploadedUrls[p.key];
            const file = uploadedUrl ? null : this.placementFile(p.key);
            const fromLibrary = this.libraryChoice(p.key);
            if (uploadedUrl) properties['_Visual'] = uploadedUrl;
            else if (!file && fromLibrary) properties['_Visual'] = fromLibrary.url;
            return { id: p.variantId, quantity: pieces, properties, file };
          });
      }

      // One "Rush production" line for as many pieces as the item, priced at 20% of the item per piece.
      rushItems(sharedText) {
        const rush = this.rushPerPiece();
        const pieces = this.combinedQuantity;
        if (!rush || !pieces) return [];
        const forLabel = [this.rules.productTitle, this.state.color].filter(Boolean).join(' – ');
        return [
          {
            id: rush.variantId,
            quantity: pieces,
            properties: { For: forLabel, '_Bulk group': sharedText['_Bulk group'], _Addon: 'Rush production' },
          },
        ];
      }

      // POST to /cart/add.js. Out of stock: Shopify adds what it can, the cart page shows it with its message.
      async postCart(url, body, contentType = null) {
        const headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' };
        if (contentType) headers['Content-Type'] = contentType;
        const response = await fetch(url, { method: 'POST', headers, body }).then((r) => r.json());
        if (response.status) {
          const message = response.description || response.message || '';
          if (/due to availability|maximum quantity/i.test(message)) return { redirect: true };
          throw new Error(message || this.str('addError'));
        }
        return response;
      }

      placementFile(key) {
        const row = this.placementUploadRows?.find((r) => r.dataset.placementKey === key);
        return row?.querySelector('[data-placement-file]')?.files?.[0] || null;
      }

      // A design file is sent as soon as it is chosen, while the customer goes on configuring: Shopify takes a few
      // seconds per file, which made "Add to cart" slow. It goes up in a throwaway cart (no cookies: the customer's
      // cart is not touched, nothing to remove, several files at once), and "Add to cart" then sends its address,
      // like a file reused from a past order.
      preUpload(key, file) {
        const placement = this.rules.placements.find((p) => p.key === key);
        const variantId = placement?.variantId || this.variants.find((v) => v.available)?.id;
        if (!variantId) return;
        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

        const body = new FormData();
        body.append('items[0][id]', variantId);
        body.append('items[0][quantity]', '1');
        body.append('items[0][properties][_Upload]', file.name);
        body.append('items[0][properties][_Visual]', file);
        const job = fetch(addUrl, { method: 'POST', credentials: 'omit', headers: { Accept: 'application/json' }, body })
          .then((response) => response.json())
          .then((data) => {
            const url = data?.items?.[0]?.properties?._Visual;
            return typeof url === 'string' && /^https?:\/\//.test(url) ? url : null;
          })
          .catch(() => null); // the file itself is then sent with "Add to cart", as before
        this.preUploads = this.preUploads || new Map();
        this.preUploads.set(key, { file, job });
      }

      // Addresses of the chosen files already sent in the background, by placement (waits for uploads in progress).
      async uploadedFileUrls() {
        const urls = {};
        const selected = this.getSelectedPlacements();
        const jobs = selected.map((p) => {
          const entry = this.preUploads?.get(p.key);
          const file = this.placementFile(p.key);
          return entry && file && entry.file === file ? entry.job : Promise.resolve(null);
        });
        (await Promise.all(jobs)).forEach((url, index) => {
          if (url) urls[selected[index].key] = url;
        });
        return urls;
      }

      getPlacementSizeLabel(key) {
        const group = this.querySelector(`[data-placement-sizes][data-placement-key="${key}"]`);
        const pressed = group?.querySelector('[data-design-size-option][aria-pressed="true"]');
        return pressed?.querySelector('strong')?.textContent || null;
      }

      // Size label with its print dimensions for the order, e.g. "Small (width ~3.5 in)".
      getPlacementSizeDetail(key) {
        const group = this.querySelector(`[data-placement-sizes][data-placement-key="${key}"]`);
        const pressed = group?.querySelector('[data-design-size-option][aria-pressed="true"]');
        const label = pressed?.querySelector('strong')?.textContent?.trim();
        if (!label) return null;
        const note = pressed.textContent.replace(label, '').replace(/^[\s-]+/, '').trim();
        return note ? `${label} (${note})` : label;
      }

      togglePricingTable() {
        if (!this.pricingToggle || !this.pricingCollapse) return;
        const isOpen = this.pricingToggle.getAttribute('aria-expanded') === 'true';

        if (isOpen) {
          this.pricingToggle.setAttribute('aria-expanded', 'false');
          this.pricingCollapse.classList.remove('is-open');
        } else {
          this.pricingToggle.setAttribute('aria-expanded', 'true');
          requestAnimationFrame(() => this.pricingCollapse.classList.add('is-open'));
        }
      }

      toggleRecap() {
        if (!this.recapDetails || !this.recapCollapse) return;

        if (this.recapDetails.open) {
          this.recapDetails.classList.remove('is-open');
          const onEnd = (evt) => {
            if (evt.target !== this.recapCollapse) return;
            this.recapDetails.open = false;
            this.recapCollapse.removeEventListener('transitionend', onEnd);
          };
          this.recapCollapse.addEventListener('transitionend', onEnd);
        } else {
          this.recapDetails.open = true;
          requestAnimationFrame(() => this.recapDetails.classList.add('is-open'));
        }
      }

      /* --------------------------------------------------------------- price */

      resolveTier(quantity) {
        const tiers = this.rules.quantityTiers;
        if (!tiers.length) return null;
        let applicable = tiers[0];
        for (const tier of tiers) {
          if (quantity >= tier.minQuantity) applicable = tier;
        }
        return applicable;
      }

      priceForSize(size) {
        const variant = this.variantForSize(size);
        return (variant?.price || 0) / 100;
      }

      referenceBasePrice() {
        const firstEntry = this.state.sizeQuantities.entries().next().value;
        if (firstEntry) return this.priceForSize(firstEntry[0]);
        const anyVariant = this.variants.find((v) =>
          this.colorPosition ? v[`option${this.colorPosition}`] === this.state.color : true
        );
        return (anyVariant?.price || 0) / 100;
      }

      baseSizePrice() {
        const price = this.priceForSize('S');
        return price > 0 ? price : this.referenceBasePrice();
      }

      sizeSurchargeBreakdown() {
        const basePrice = this.baseSizePrice();
        const entries = [];
        this.state.sizeQuantities.forEach((qty, size) => {
          if (qty <= 0) return;
          const extraPerUnit = this.priceForSize(size) - basePrice;
          if (extraPerUnit > 0.001) {
            entries.push({ size, qty, extraTotal: extraPerUnit * qty });
          }
        });
        return entries;
      }

      updateSizeSurchargeNote() {
        if (!this.sizeSurchargeNote || !this.sizeSurchargeText) return;
        const entries = this.sizeSurchargeBreakdown();
        if (!entries.length) {
          this.sizeSurchargeNote.hidden = true;
          return;
        }
        const template = this.str('surchargeLine');
        const text = entries
          .map(({ size, qty, extraTotal }) =>
            template
              .replace('%qty%', qty)
              .replace('%size%', size)
              .replace('%amount%', this.formatMoney(extraTotal))
          )
          .join(', ');
        this.sizeSurchargeText.textContent = text;
        this.sizeSurchargeNote.hidden = false;
      }

      calculatePrice() {
        const combined = this.combinedQuantity;
        const tier = this.resolveTier(Math.max(combined, this.minCombined));
        const discountPercent = tier?.discountPercent || 0;
        const placementsExtra = this.getSelectedPlacements().reduce(
          (sum, p) => sum + (p.priceModifier || 0),
          0
        );
        const modifierPercent = 0;
        const rushExtra = this.rushPerPiece()?.price || 0;

        let total = 0;
        let originalTotal = 0;
        this.state.sizeQuantities.forEach((qty, size) => {
          const base = this.priceForSize(size);
          const unit = ((base + placementsExtra + rushExtra) * (100 - discountPercent)) / 100;
          total += unit * qty;
          originalTotal += (base + placementsExtra + rushExtra) * qty;
        });

        const refBase = this.referenceBasePrice();
        const fallbackUnitPrice = ((refBase + placementsExtra + rushExtra) * (100 - discountPercent)) / 100;
        const unitPrice = combined > 0 ? total / combined : fallbackUnitPrice;

        const netDiscountPercent =
          originalTotal > 0 ? Math.round(((originalTotal - total) / originalTotal) * 100) : 0;

        return {
          tier,
          discountPercent,
          unitPrice,
          total,
          originalTotal,
          netDiscountPercent,
          placementsExtra,
          modifierPercent,
          basePrice: refBase,
        };
      }

      updateTierPrices() {
        const basePrice = this.baseSizePrice();
        const placementsExtra = this.getSelectedPlacements().reduce(
          (sum, p) => sum + (p.priceModifier || 0),
          0
        );
        const combined = this.combinedQuantity;
        const totalSurcharge = this.sizeSurchargeBreakdown().reduce(
          (sum, entry) => sum + entry.extraTotal,
          0
        );
        const avgSurchargePerUnit = combined > 0 ? totalSurcharge / combined : 0;
        const blendedBase = basePrice + avgSurchargePerUnit + (this.rushPerPiece()?.price || 0);

        this.tierRows.forEach((row) => {
          const discountPercent = parseFloat(row.dataset.discountPercent) || 0;
          const price = ((blendedBase + placementsExtra) * (100 - discountPercent)) / 100;
          const priceCell = row.querySelector('[data-tier-price-cell]');
          if (priceCell) priceCell.textContent = this.formatMoney(price);
          row.dataset.tierUnitPrice = price;
        });
      }

      formatMoney(amount) {
        try {
          return new Intl.NumberFormat(this.rules.locale, {
            style: 'currency',
            currency: this.rules.currency,
          }).format(amount);
        } catch {
          return `${amount.toFixed(2)} ${this.rules.currency}`;
        }
      }

      /* ---------------------------------------------------------- validation */

      getValidationIssue() {
        if (!this.state.color) return 'generic';
        if (this.combinedQuantity < this.minCombined) return 'generic';

        for (const [size, qty] of this.state.sizeQuantities) {
          const variant = this.variantForSize(size);
          if (!variant || variant.available === false) return 'generic';
          const stock = this.stockForSize(size);
          if (Number.isFinite(stock) && qty > stock) return 'generic';
        }

        if (this.state.printMethod === 'dtf') {
          if (this.state.placements.size === 0) return 'design';

          for (const key of this.state.placements) {
            const uploadRow = this.placementUploadRows.find((r) => r.dataset.placementKey === key);
            const fileInput = uploadRow?.querySelector('[data-placement-file]');
            if (!fileInput?.files?.length && !uploadRow?.dataset.libraryUrl) return 'design';
          }
        }

        return null;
      }

      isConfigurationValid() {
        return this.getValidationIssue() === null;
      }

      /* -------------------------------------------------------------- render */

      breakdownString() {
        return Array.from(this.state.sizeQuantities.entries())
          .map(([size, qty]) => `${size} × ${qty}`)
          .join(', ');
      }

      recalculate() {
        const combined = this.combinedQuantity;
        const { unitPrice, total, originalTotal, netDiscountPercent, tier } = this.calculatePrice();

        this.updateTierPrices();

        this.combinedQtyEls.forEach((el) => {
          el.textContent = combined.toLocaleString(this.rules.locale);
        });
        if (this.combinedHintEl) {
          this.combinedHintEl.hidden = combined === 0 || combined >= this.minCombined;
        }

        this.updateSizeSurchargeNote();

        this.tierRows.forEach((row) => {
          const isActive =
            combined > 0 && parseInt(row.dataset.minQuantity, 10) === tier?.minQuantity;
          row.classList.toggle('is-selected', isActive);
        });

        if (this.totalPriceEl) this.totalPriceEl.textContent = combined > 0 ? this.formatMoney(total) : '—';
        this.unitPriceEls.forEach((el) => {
          el.textContent = this.formatMoney(unitPrice);
        });

        const hasNetDiscount = netDiscountPercent > 0 && combined > 0;
        if (this.totalPriceOriginalEl) {
          this.totalPriceOriginalEl.hidden = !hasNetDiscount;
          if (hasNetDiscount) this.totalPriceOriginalEl.textContent = this.formatMoney(originalTotal);
        }
        if (this.quantityDiscountBadgeEl) {
          this.quantityDiscountBadgeEl.hidden = !hasNetDiscount;
          if (hasNetDiscount) this.quantityDiscountBadgeEl.textContent = `-${netDiscountPercent}%`;
        }

        this.updateRecap();
        this.updateHiddenProperties();

        const issue = this.getValidationIssue();
        const valid = issue === null;
        if (this.submitButton) this.submitButton.disabled = !valid || this.submitting;
        if (this.noticeEl) {
          if (issue === 'design' && this.noticeDesignText) {
            this.noticeEl.textContent = this.noticeDesignText;
          } else if (this.noticeDefaultText) {
            this.noticeEl.textContent = this.noticeDefaultText;
          }
          this.noticeEl.hidden = valid;
        }
      }

      updateRecap() {
        if (this.recapColor) this.recapColor.textContent = this.state.color || '—';
        if (this.recapSize) this.recapSize.textContent = this.breakdownString() || '—';

        if (this.recapPrintMethod) {
          const selectedBtn = this.printMethodButtons.find(
            (btn) => btn.dataset.value === this.state.printMethod
          );
          this.recapPrintMethod.textContent = selectedBtn?.querySelector('strong')?.textContent || '—';
        }

        // Under "Print Location(s)", one row per placement: "- Front (Large)" on the left, its file name on the
        // right (cut if too long).
        if (this.recapPlacementsEmpty) {
          this.querySelectorAll('[data-recap-placement-row]').forEach((row) => row.remove());
          const selected = this.getSelectedPlacements();
          // "Print Location(s)" stays as the heading: "Blank textile" when none, then the placements below it.
          const emptyValue = this.recapPlacementsEmpty.querySelector('dd');
          this.recapPlacementsDefault = this.recapPlacementsDefault ?? emptyValue?.textContent;
          if (emptyValue) emptyValue.textContent = selected.length ? '' : this.recapPlacementsDefault;
          let previous = this.recapPlacementsEmpty;
          selected.forEach((p) => {
            const row = document.createElement('div');
            row.dataset.recapPlacementRow = '';
            row.className = 'product-configurator__recap-placement';
            const sizeLabel = this.getPlacementSizeLabel(p.key);
            const label = document.createElement('dt');
            label.textContent = `- ${sizeLabel ? `${p.label} (${sizeLabel})` : p.label}`;
            const fileName = this.placementFileName(p.key);
            const file = document.createElement('dd');
            file.className = 'product-configurator__recap-file';
            file.textContent = fileName || '—';
            if (fileName) file.title = fileName;
            row.append(label, file);
            previous.after(row);
            previous = row;
          });
        }

        if (this.recapQuantity) {
          this.recapQuantity.textContent = this.combinedQuantity.toLocaleString(this.rules.locale);
        }

        if (this.recapProduction) {
          const label = this.productionLabel();
          this.recapProduction.textContent = label || '—';
          if (this.recapProductionRow) this.recapProductionRow.hidden = !label;
        }

        if (this.recapLeadtime) {
          const leadTime = this.getSelectedLeadTime();
          this.recapLeadtime.textContent = leadTime ? `${leadTime.label} (${leadTime.durationLabel})` : '—';
        }
      }

      ensureBulkGroupId() {
        if (!this.bulkGroupId) {
          this.bulkGroupId = (() => {
            // Short readable code shared by all lines of one configured item, e.g. "#A7K3"
            // (no 0/O/1/I, to read it easily on orders).
            const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
            const values = new Uint8Array(4);
            if (window.crypto?.getRandomValues) window.crypto.getRandomValues(values);
            else values.forEach((_, i) => (values[i] = Math.floor(Math.random() * 256)));
            return '#' + Array.from(values, (v) => alphabet[v % alphabet.length]).join('');
          })();
        }
        return this.bulkGroupId;
      }

      // "Left Chest (Small, width ~3.5 in)": placement with its size and print width, for the orders in the admin.
      placementPrintLabel(p) {
        // "Small (width ~3.5 in)" -> "Small, width ~3.5 in": the size label comes first, before the comma.
        const sizeDetail = this.getPlacementSizeDetail(p.key)?.replace(/ \((.*)\)$/, ', $1');
        return sizeDetail ? `${p.label} (${sizeDetail})` : p.label;
      }

      sharedProperties() {
        // Short lines for the order in the admin (keys starting with "_" are hidden from the customer at checkout):
        //   _Bulk group: #SH8U
        //   _Print method: DTF Printing · Rush (6–7 business days)
        //   _Print 1: Left Chest (Small, width ~3.5 in): logo.png
        //   _Print 2: Back (Large, width ~11 in): text.pdf
        // The cart, the account area, reorder and the emails read them back (older orders: "_Print locations",
        // "_Designs" and "_Production").
        // Sizes/quantities live on the cart lines themselves, so they stay right when edited in the cart.
        const props = { '_Bulk group': this.ensureBulkGroupId() };

        const printMethodBtn = this.printMethodButtons.find((btn) => btn.dataset.value === this.state.printMethod);
        const printMethodLabel = printMethodBtn?.querySelector('strong')?.textContent?.trim();
        if (printMethodLabel && this.state.printMethod !== 'none') {
          const production = this.productionLabel() || 'Standard (10–15 business days)';
          props['_Print method'] = `${printMethodLabel} · ${production}`;
        }

        this.getSelectedPlacements().forEach((p, index) => {
          const fileName = this.placementFileName(p.key);
          const placement = this.placementPrintLabel(p);
          props[`_Print ${index + 1}`] = fileName ? `${placement}: ${fileName}` : placement;
        });

        const leadTime = this.getSelectedLeadTime();
        if (leadTime) props['Lead time'] = `${leadTime.label} (${leadTime.durationLabel})`;

        return props;
      }

      updateHiddenProperties() {
        const hasSizes = this.state.sizeQuantities.size > 0;

        const setField = (field, value) => {
          if (!field) return;
          const has = value != null && value !== '';
          field.value = has ? value : '';
          field.disabled = !has;
        };

        const selected = this.getSelectedPlacements();
        setField(this.placementsSummaryField, selected.map((p) => p.label).join(', '));

        const leadTime = this.getSelectedLeadTime();
        setField(this.leadtimeSummaryField, leadTime ? `${leadTime.label} (${leadTime.durationLabel})` : '');

        setField(this.breakdownField, this.breakdownString());
        setField(this.bulkGroupField, hasSizes ? this.ensureBulkGroupId() : '');
        setField(this.combinedQtyField, hasSizes ? String(this.combinedQuantity) : '');

        const { unitPrice, total, tier, placementsExtra, modifierPercent } = this.calculatePrice();
        setField(this.unitPriceField, hasSizes ? unitPrice.toFixed(2) : '');

        const firstEntry = this.state.sizeQuantities.entries().next().value;
        if (this.variantIdField) {
          const firstVariant = firstEntry ? this.variantForSize(firstEntry[0]) : null;
          this.variantIdField.value = firstVariant?.id || '';
          this.variantIdField.disabled = !firstVariant;
        }
        if (this.quantityField) this.quantityField.value = firstEntry ? firstEntry[1] : 0;

        if (this.debugSummaryField) {
          this.debugSummaryField.value = JSON.stringify({
            color: this.state.color,
            sizes: Object.fromEntries(this.state.sizeQuantities),
            combinedQuantity: this.combinedQuantity,
            tierMinQuantity: tier?.minQuantity,
            placements: Array.from(this.state.placements),
            leadTimeId: this.state.leadTimeId,
            placementsExtra,
            leadTimeModifierPercent: modifierPercent,
            unitPrice: Math.round(unitPrice * 100) / 100,
            total: Math.round(total * 100) / 100,
          });
        }
      }

      /* --------------------------------------------------------- add to cart */

      handleSubmitCapture(evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation();

        this.recalculate();
        if (!this.isConfigurationValid()) {
          if (this.noticeEl) this.noticeEl.hidden = false;
          this.noticeEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.addToCart();
      }

      setSubmitting(state) {
        this.submitting = state;
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('loading', state);
        this.submitButton.setAttribute('aria-disabled', state ? 'true' : 'false');
        this.submitButton.disabled = state;
        this.querySelector('.loading__spinner')?.classList.toggle('hidden', !state);
        if (!state) this.recalculate();
      }

      showError(message) {
        if (this.errorWrapper) this.errorWrapper.hidden = !message;
        if (this.errorMessageEl) this.errorMessageEl.textContent = message || '';
        if (message) this.errorWrapper?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      sectionsToRender() {
        if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return null;
        return this.cart.getSectionsToRender().map((section) => section.id);
      }

      async addToCart() {
        const entries = Array.from(this.state.sizeQuantities.entries()).filter(([, qty]) => qty > 0);
        if (!entries.length) return;

        this.showError(null);
        this.setSubmitting(true);

        const sharedText = this.sharedProperties();
        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

        let leaving = false;
        try {
          // Sizes + placements + artwork files in a single request (no follow-up request, no section rendering).
          const garments = entries.map(([size, qty]) => ({
            id: this.variantForSize(size)?.id,
            quantity: qty,
            properties: { ...sharedText },
          }));
          if (garments.some((item) => !item.id)) throw new Error(this.str('addError'));

          const comments = new FormData(this.form).get('properties[Comments]');
          if (comments) garments[0].properties.Comments = comments;

          // Files chosen earlier are already sent (see preUpload): only their addresses go with the items.
          const uploadedUrls = await this.uploadedFileUrls();
          const placements = this.placementItems(sharedText, uploadedUrls);

          // Placements with no "Custom printing" variant: their files are uploaded on the first garment line.
          this.getSelectedPlacements()
            .filter((p) => !p.variantId)
            .forEach((p) => {
              const file = this.placementFile(p.key);
              const fromLibrary = this.libraryChoice(p.key);
              if (uploadedUrls[p.key]) garments[0].properties[`_Visual - ${p.label}`] = uploadedUrls[p.key];
              else if (file) garments[0].files = { ...(garments[0].files || {}), [`_Visual - ${p.label}`]: file };
              else if (fromLibrary) garments[0].properties[`_Visual - ${p.label}`] = fromLibrary.url;
            });

          // Everything in a single request: each artwork file is uploaded once, on its "Custom printing" line.
          const formData = new FormData();
          garments.concat(placements, this.rushItems(sharedText)).forEach((item, index) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, String(item.quantity));
            Object.entries(item.properties).forEach(([key, value]) => {
              formData.append(`items[${index}][properties][${key}]`, value);
            });
            if (item.file) formData.append(`items[${index}][properties][_Visual]`, item.file);
            Object.entries(item.files || {}).forEach(([key, file]) => {
              formData.append(`items[${index}][properties][${key}]`, file);
            });
          });

          const response = await this.postCart(addUrl, formData);
          if (response.redirect) {
            leaving = true;
            window.location = cartUrl;
            return;
          }

          // Start a fresh run so the same artwork is not re-added by accident.
          this.bulkGroupId = null;
          this.updateHiddenProperties();
          leaving = true;
          window.location = cartUrl;
        } catch (error) {
          const message = error.message || this.str('addError');
          if (!/maximum quantity/i.test(message) && !/due to availability/i.test(message)) {
            this.showError(message);
          }
        } finally {
          // Keep the button in its loading state while the cart page opens.
          if (!leaving) this.setSubmitting(false);
        }
      }
    }
  );
}
